import {
  Button,
  MenuItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router';

import { errorToastConfig } from 'packages/common-frontend/constants';
import { FacilityDetailsRouteParamsStruct } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/helper';
import { TextInput } from '~/core/routes/settings/implementations/common/Inputs';
import { GET_CREDIT_AGREEMENT_DOCUMENTS_METADATA } from '~/core/routes/settings/implementations/implementations.gql';
import { useUpdateCreditAgreementDisplayNameMutation } from '~/core/routes/settings/implementations/implementations.gql.generated';

type FormData = {
  newName: string;
};

export const RenameCreditAgreementModal = ({
  creditAgreementId,
  existingDisplayName,
}: {
  creditAgreementId: string;
  existingDisplayName?: string;
}) => {
  const toast = useToast();
  const params = useParams();
  const { facilityId } = FacilityDetailsRouteParamsStruct.parse(params);
  const disclosure = useDisclosure();

  const form = useForm<FormData>({
    defaultValues: { newName: existingDisplayName },
  });

  useEffect(() => {
    if (disclosure.isOpen) form.reset({ newName: existingDisplayName });
  }, [disclosure.isOpen, form, existingDisplayName]);

  const [
    updateClientCreditAgreementName,
  ] = useUpdateCreditAgreementDisplayNameMutation({
    refetchQueries: [
      {
        query: GET_CREDIT_AGREEMENT_DOCUMENTS_METADATA,
        variables: { creditFacilityId: facilityId },
      },
    ],
  });

  const changeCreditAgreementName = async (displayName: string) => {
    try {
      await updateClientCreditAgreementName({
        variables: {
          creditAgreementId,
          displayName,
        },
      });
    } catch (err) {
      Sentry.captureException(err);
      toast({
        ...errorToastConfig,
        title: 'Error updating credit agreement name.',
      });
    }

    disclosure.onClose();
  };

  return (
    <>
      <MenuItem onClick={disclosure.onOpen}>Name this Version</MenuItem>

      <Modal {...disclosure} size="lg">
        <ModalOverlay />
        <ModalContent>
          <FormProvider {...form}>
            <form
              onSubmit={form.handleSubmit((data) => {
                changeCreditAgreementName(data.newName);
              })}
            >
              <ModalHeader fontSize="lg" fontWeight="bold">
                Name this Version
              </ModalHeader>

              <ModalBody>
                <TextInput<FormData> name="newName" label="Name" />
              </ModalBody>

              <ModalFooter>
                <Button onClick={disclosure.onClose}>Cancel</Button>
                <Button type="submit" colorScheme="v2-aurora" ml={3}>
                  Save
                </Button>
              </ModalFooter>
            </form>
          </FormProvider>
        </ModalContent>
      </Modal>
    </>
  );
};
